/* ============================================================
   PARTNER STUDIO — pemilih produk test lab (modal)
   ============================================================
   Isi SAMA pemilih makmal v1 (/pipeline-test → PipelineTestView "Pilih produk dari
   library"): tab System / My Products dengan bilangan (susunan + lalai SAMA makmal v1) · kotak cari · susunan
   Number / Name / Newest · gambar kecil · #nombor · harga + "has link" · tanda
   READY / PENDING. Rupa v3.9 (`.s-modal`, token `--s-*`) — arahan user 27 Okt:
   "kenapa list dropdown dlm page test lab partner tu tak cantik mcm makmal v1".

   Data datang dari hooks/useProdukLab.js (dimuat sekali oleh test lab). Produk
   PENDING boleh dipilih (sama makmal) — test lab sendiri menghalang Run dengan
   sebab yang jelas.
   ⚠️ KEMBAR logik susunan "date" = PipelineTestView filteredItems (updatedAt → addedAt).
   ============================================================ */
import { useEffect, useMemo, useState } from 'react';
import { X, Search, Image as IkonGambar } from 'lucide-react';
import { Badge, Segmented } from '../design/ui';
import PilihV39 from './PilihV39';
import { produkSedia } from '../hooks/useProdukLab';

const SUSUNAN = [
    // Susunan pilihan SAMA makmal v1 (Number · Name · Newest); lalai Newest (makmal: 'Terbaru').
    { id: 'number', label: 'Sort: Number' },
    { id: 'name', label: 'Sort: Name' },
    { id: 'date', label: 'Sort: Newest' },
];
const masaItem = (it) => it.updatedAt?.toDate?.() || it.addedAt?.toDate?.() || new Date(0);

/** Gambar kecil untuk senarai — URL atau data URI sahaja (base64 mentah tanpa jenis tidak diteka). */
function gambarSenarai(it) {
    const url = String(it?.imageUrl || '').trim();
    if (/^https?:\/\//.test(url) || url.startsWith('data:image/')) return url;
    const b64 = String(it?.imageBase64 || '').trim();
    return b64.startsWith('data:image/') ? b64 : '';
}

export function GambarKecil({ it, saiz = 40 }) {
    const src = gambarSenarai(it);
    return (
        <div style={{ width: saiz, height: saiz, borderRadius: 8, flex: 'none', overflow: 'hidden', border: '1px solid var(--s-line)', background: 'var(--s-fill)', display: 'grid', placeItems: 'center', color: 'var(--s-ink-3)' }}>
            {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IkonGambar size={Math.round(saiz * 0.42)} aria-hidden="true" />}
        </div>
    );
}

export default function PartnerPilihProduk({ produkList, produkId, onPilih, onTutup }) {
    // SAMA makmal v1 + page My Products (arahan user 29 Okt): tab System dahulu dan dibuka lalai.
    const [tab, setTab] = useState('system');
    const [cari, setCari] = useState('');
    const [susun, setSusun] = useState('date');

    useEffect(() => {
        const kekunci = (e) => { if (e.key === 'Escape') onTutup(); };
        window.addEventListener('keydown', kekunci);
        return () => window.removeEventListener('keydown', kekunci);
    }, [onTutup]);

    const senarai = useMemo(() => {
        const asas = tab === 'own' ? produkList.sendiri : produkList.sampel;
        const q = cari.trim().toLowerCase();
        const hasil = q ? asas.filter((it) => (it.name || '').toLowerCase().includes(q)) : [...asas];
        if (susun === 'name') hasil.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        else if (susun === 'date') hasil.sort((a, b) => masaItem(b) - masaItem(a));
        else hasil.sort((a, b) => (a.number || 0) - (b.number || 0));
        return hasil;
    }, [tab, cari, susun, produkList]);

    return (
        <div className="s-root s-modal" role="dialog" aria-modal="true" aria-label="Choose a product" onClick={onTutup}>
            <div className="s-modal__panel" style={{ maxWidth: 680, height: 'min(640px, 88vh)' }} onClick={(e) => e.stopPropagation()}>
                <header className="s-modal__head">
                    <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>Choose a product</h2>
                    <button type="button" className="s-modal__x" aria-label="Close" onClick={onTutup}><X size={15} strokeWidth={2} /></button>
                </header>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--s-line)', display: 'flex', flexDirection: 'column', gap: 10, flex: 'none' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <Segmented value={tab} onChange={setTab} options={[
                            { value: 'system', label: `System (${produkList.sampel.length})` },
                            { value: 'own', label: `My Products (${produkList.sendiri.length})` },
                        ]} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
                            <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--s-ink-3)' }} />
                            <input className="s-input" style={{ paddingLeft: 30, width: '100%' }} value={cari} autoFocus
                                onChange={(e) => setCari(e.target.value)} placeholder="Search product name…" aria-label="Search product name" />
                        </div>
                        <PilihV39 nilai={susun} pilihan={SUSUNAN} onPilih={setSusun} lebar="auto" jajarKanan ariaLabel="Sort" />
                    </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 8px' }}>
                    {!produkList.dimuat && <p className="s-hint" style={{ textAlign: 'center', padding: 28 }}>Loading products…</p>}
                    {produkList.dimuat && senarai.length === 0 && (
                        <p className="s-hint" style={{ textAlign: 'center', padding: 28 }}>
                            {cari.trim() ? 'No product matches that name.' : tab === 'own' ? 'No products yet. Add one in My Products first.' : 'No System products.'}
                        </p>
                    )}
                    {senarai.map((it) => {
                        const sedia = produkSedia(it);
                        const dipilih = it.id === produkId;
                        return (
                            <button key={it.id} type="button" onClick={() => onPilih(it.id)}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderRadius: 10,
                                    border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit',
                                    background: dipilih ? 'var(--s-fill)' : 'transparent',
                                }}
                                onMouseEnter={(e) => { if (!dipilih) e.currentTarget.style.background = 'var(--s-fill)'; }}
                                onMouseLeave={(e) => { if (!dipilih) e.currentTarget.style.background = 'transparent'; }}>
                                <GambarKecil it={it} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="s-cardname" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {it.number ? `#${it.number} · ` : ''}{it.name || '(no name)'}
                                    </div>
                                    <div className="s-dim" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {it.price || 'no price'}{it.link ? ' · has link' : ''}{dipilih ? ' · selected' : ''}
                                    </div>
                                </div>
                                <Badge tone={sedia ? 'ok' : 'warn'}>{sedia ? 'READY' : 'PENDING'}</Badge>
                            </button>
                        );
                    })}
                </div>
                {produkList.ralat && <p className="s-hint" style={{ margin: 0, padding: '8px 16px', color: 'var(--s-stop)', borderTop: '1px solid var(--s-line)' }}>{produkList.ralat}</p>}
            </div>
        </div>
    );
}
